/**
 * 内联 ```references 代码围栏解析。
 *
 * 新版 RAG 服务把引用清单先以 Markdown 代码围栏的形式内联进答案正文里：
 *
 *   ```references
 *   [ { "id": "[1]", "documentId": "...", "filename": "...",
 *       "chunkId": "...", "heading": "...", "excerpt": "..." }, ... ]
 *   ```
 *
 * 之后才再单独下发 `event: references`（携带 segment / relatedDocumentIds 等更全的元数据）。
 *
 * 前端展示时不能把这段 JSON 原文显示在回答里——本工具把围栏从答案文本中剥离，
 * 同时把围栏内的 JSON 数组解析出来用于“内联块建表”。围栏可能尚未闭合（流式途中），
 * 此时同样从 ```references 起整体隐藏；JSON 不完整解析失败则先不出引用，等闭合后再解析。
 */

import { unescapeBasic } from "./answer";

// 已闭合：```references\n[ ... ]```
const CLOSED_FENCE = /```[ \t]*references[ \t]*\r?\n?([\s\S]*?)```/i;
// 仅有开头（流式途中尚未闭合）：```references\n[ ...
const OPEN_FENCE = /```[ \t]*references[ \t]*\r?\n?([\s\S]*)$/i;

/** 尝试把整段解析成数组；未包裹是裸 JSON，被 {"answer":...} 包裹时引号为 \" 需反转义。 */
function tryParseArray(text: string): any[] | null {
  for (const candidate of [text, unescapeBasic(text)]) {
    try {
      const parsed = JSON.parse(candidate);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * 扫描（可能未闭合的）数组文本，逐个提取顶层已闭合的对象 {...} 并解析。
 * 用于流式途中：每攒够一条完整记录就先显示一条，不必等整个数组输出完。
 */
function scanCompleteObjects(body: string): any[] {
  const start = body.indexOf("[");
  if (start < 0) return [];
  const out: any[] = [];
  let depth = 0;
  let objStart = -1;
  let inStr = false;
  let esc = false;
  for (let i = start + 1; i < body.length; i++) {
    const c = body[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === "\\") esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') {
      inStr = true;
    } else if (c === "{" || c === "[") {
      if (c === "{" && depth === 0) objStart = i;
      depth++;
    } else if (c === "}" || c === "]") {
      depth--;
      if (c === "}" && depth === 0 && objStart >= 0) {
        try {
          out.push(JSON.parse(body.slice(objStart, i + 1)));
        } catch {
          /* 该对象解析失败则跳过 */
        }
        objStart = -1;
      }
    }
  }
  return out;
}

function parseReferencesJson(body: string): any[] {
  const trimmed = (body ?? "").trim();
  if (!trimmed) return [];
  // 1) 先整段解析（围栏已闭合、数组完整）
  const whole = tryParseArray(trimmed);
  if (whole) return whole;
  // 2) 流式途中数组尚未闭合：增量提取已完整的对象（未包裹按原文扫描；
  //    被 JSON 壳包裹的转义文本反转义一层后再扫描）
  const partial = scanCompleteObjects(trimmed);
  if (partial.length > 0) return partial;
  const unescaped = unescapeBasic(trimmed);
  return unescaped !== trimmed ? scanCompleteObjects(unescaped) : [];
}

export interface SplitReferencesResult {
  /** 去掉 ```references 围栏后的答案文本 */
  text: string;
  /** 围栏内解析出的引用数组（原始字段形状）；解析失败或无围栏时为空 */
  references: any[];
  /** 是否正处于一个尚未闭合的 ```references 围栏中（流式途中，引用仍在构建） */
  building: boolean;
}

/**
 * 从答案文本中剥离 ```references 围栏，返回去围栏后的文本、解析出的引用数组，
 * 以及围栏是否尚未闭合（building，用于流式途中显示“正在构建 References …”）。
 * 输入应为“已去掉外层 {"answer":...} 包裹”的答案文本（与 extractStreamingAnswerView 的输出衔接）。
 */
export function splitReferencesFence(text: string): SplitReferencesResult {
  const input = text ?? "";
  // 没有围栏直接返回，避免无谓的正则
  if (!input.includes("```") || !/references/i.test(input)) {
    return { text: input, references: [], building: false };
  }

  let cleaned = input;
  let body: string | null = null;
  let building = false;

  const closed = input.match(CLOSED_FENCE);
  if (closed && closed.index !== undefined) {
    cleaned = input.slice(0, closed.index) + input.slice(closed.index + closed[0].length);
    body = closed[1];
  } else {
    const open = input.match(OPEN_FENCE);
    if (open && open.index !== undefined) {
      cleaned = input.slice(0, open.index);
      body = open[1];
      // 只有开头、还没等到闭合的 ```：引用仍在流式构建中
      building = true;
    }
  }

  const references = body != null ? parseReferencesJson(body) : [];
  // 剥离围栏后常残留尾部空行，收尾清理一下；正文前缀保持不动
  return {
    text: body != null ? cleaned.replace(/\s+$/, "") : cleaned,
    references,
    building,
  };
}
